import {
  getProject,
  patchProjectSettings,
  type StudioProjectSettingsRevisionRef,
} from '@/services/project-api'
import { useStudioStore } from '@/store/studio-store'
import {
  cogoXyzRegistry,
  isCogoXyzRegistrySnapshot,
  type CogoXyzRegistry,
  type CogoXyzRegistrySnapshot,
} from './cogo-xyz-registry'
import { pushCogoXyzRegistrySnapshotToServer } from './cogo-xyz-server-sync'

export const COGO_XYZ_PROJECT_SETTINGS_KEY = 'cogoXyzRegistry'

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

export function readCogoXyzRegistrySnapshotFromSettings(
  settings: unknown,
): CogoXyzRegistrySnapshot | null {
  const snapshot = readRecord(settings)[COGO_XYZ_PROJECT_SETTINGS_KEY]
  return isCogoXyzRegistrySnapshot(snapshot) ? snapshot : null
}

export async function loadCogoXyzRegistryFromProject(
  projectId: string,
  registry: CogoXyzRegistry = cogoXyzRegistry,
): Promise<boolean> {
  const project = await getProject(projectId)
  const snapshot = readCogoXyzRegistrySnapshotFromSettings(project?.settings)
  if (!snapshot) return false
  registry.restore(snapshot)
  return true
}

type RegistryWorkItem = CogoXyzRegistrySnapshot['workItems'][number]
type RegistryToolRun = CogoXyzRegistrySnapshot['toolRuns'][number]

function toolRunBelongsToWorkItemPacket(
  snapshot: CogoXyzRegistrySnapshot,
  workItem: RegistryWorkItem,
  toolRun: RegistryToolRun,
): boolean {
  const bundleIds = new Set(
    snapshot.bundles
      .filter((record) => record.workItemId === workItem.id)
      .map((record) => record.id),
  )
  const previewUrls = new Set(
    snapshot.previews
      .filter((record) => record.workItemId === workItem.id)
      .map((record) => record.previewUrl),
  )
  const input = toolRun.input
  const output = toolRun.output ?? {}
  return input.workItemId === workItem.id ||
    output.workItemId === workItem.id ||
    (typeof input.bundleId === 'string' && bundleIds.has(input.bundleId)) ||
    (typeof output.bundleId === 'string' && bundleIds.has(output.bundleId)) ||
    (typeof input.previewUrl === 'string' && previewUrls.has(input.previewUrl)) ||
    (typeof output.previewUrl === 'string' && previewUrls.has(output.previewUrl))
}

function workItemPacketFreshness(
  snapshot: CogoXyzRegistrySnapshot,
  workItem: RegistryWorkItem,
): string {
  return [
    workItem.updatedAt,
    ...snapshot.bundles
      .filter((record) => record.workItemId === workItem.id)
      .map((record) => record.generatedAt),
    ...snapshot.previews
      .filter((record) => record.workItemId === workItem.id)
      .map((record) => record.createdAt),
    ...snapshot.evidence
      .filter((record) => record.workItemId === workItem.id)
      .map((record) => record.createdAt),
    ...snapshot.toolRuns
      .filter((toolRun) => toolRunBelongsToWorkItemPacket(snapshot, workItem, toolRun))
      .map((toolRun) => toolRun.createdAt),
  ].reduce((latest, timestamp) => timestamp > latest ? timestamp : latest)
}

export function mergeCogoXyzRegistrySnapshots(
  snapshots: CogoXyzRegistrySnapshot[],
): CogoXyzRegistrySnapshot {
  const packetWinners = new Map<string, {
    snapshot: CogoXyzRegistrySnapshot
    workItem: RegistryWorkItem
    freshness: string
  }>()
  for (const snapshot of snapshots) {
    for (const workItem of snapshot.workItems) {
      const current = packetWinners.get(workItem.id)
      const freshness = workItemPacketFreshness(snapshot, workItem)
      if (!current || freshness > current.freshness) {
        packetWinners.set(workItem.id, { snapshot, workItem, freshness })
      }
    }
  }

  const winners = [...packetWinners.values()]
  const winningToolRuns = new Map<string, CogoXyzRegistrySnapshot['toolRuns'][number]>()
  for (const { snapshot, workItem } of winners) {
    for (const toolRun of snapshot.toolRuns) {
      if (toolRunBelongsToWorkItemPacket(snapshot, workItem, toolRun)) {
        winningToolRuns.set(toolRun.id, toolRun)
      }
    }
  }

  return {
    version: 1,
    workItems: winners.map(({ workItem }) => workItem),
    bundles: winners.flatMap(({ snapshot, workItem }) =>
      snapshot.bundles.filter((record) => record.workItemId === workItem.id)),
    previews: winners.flatMap(({ snapshot, workItem }) =>
      snapshot.previews.filter((record) => record.workItemId === workItem.id)),
    evidence: winners.flatMap(({ snapshot, workItem }) =>
      snapshot.evidence.filter((record) => record.workItemId === workItem.id)),
    toolRuns: [...winningToolRuns.values()],
  }
}

/**
 * Library is a workspace surface, not a hidden view of whichever project was
 * opened last. Read every authenticated server project and restore one merged
 * snapshot so direct `/studio/library` entry and project switching expose the
 * same set of reusable work.
 */
export async function loadCogoXyzRegistryFromProjects(
  projectIds: string[],
  registry: CogoXyzRegistry = cogoXyzRegistry,
): Promise<boolean> {
  const uniqueProjectIds = [...new Set(projectIds.filter(Boolean))]
  let projectReadFailed = false
  const snapshots = (
    await Promise.all(
      uniqueProjectIds.map(async (projectId) => {
        try {
          const project = await getProject(projectId)
          if (!project) {
            projectReadFailed = true
            return null
          }
          return readCogoXyzRegistrySnapshotFromSettings(project?.settings)
        } catch (error) {
          projectReadFailed = true
          console.warn(
            `[cogo-xyz] failed to load Library snapshot for project ${projectId}`,
            error,
          )
          return null
        }
      }),
    )
  ).filter((snapshot): snapshot is CogoXyzRegistrySnapshot => snapshot !== null)

  if (projectReadFailed) return false
  if (snapshots.length === 0) return false
  // Choose one complete packet per WorkItem by its update timestamp so a
  // newer local mutation survives refresh without mixing stale server child
  // records into that packet.
  registry.restore(
    mergeCogoXyzRegistrySnapshots([...snapshots, registry.snapshot()]),
  )
  return true
}

export async function saveCogoXyzRegistryToProject(
  projectId: string,
  snapshot: CogoXyzRegistrySnapshot,
  options?: { expectedSettingsRevision: StudioProjectSettingsRevisionRef },
): Promise<StudioProjectSettingsRevisionRef> {
  const expectedSettingsRevision = options?.expectedSettingsRevision ??
    (await getProject(projectId))?.settingsRevision
  if (!expectedSettingsRevision) {
    throw new Error(`COGO_XYZ_SETTINGS_REVISION_UNAVAILABLE:${projectId}`)
  }
  const result = await patchProjectSettings(
    projectId,
    { [COGO_XYZ_PROJECT_SETTINGS_KEY]: snapshot },
    { expectedSettingsRevision },
  )
  return result.settingsRevision
}

export type GeneratedRegistryPersistence =
  | {
      status: 'saved'
      projectId: string
      retryable: false
    }
  | {
      status: 'sync_pending'
      projectId: string
      retryable: true
      error: string
      expectedSettingsRevision?: StudioProjectSettingsRevisionRef
    }

/**
 * The generated project already exists before this registry overlay runs.
 * Report a retryable sync state instead of turning a metadata failure into a
 * generation failure that would cause callers to create the project again.
 * Repeating this helper is idempotent because it patches the same owned key
 * with the same immutable snapshot.
 */
export async function persistGeneratedCogoXyzRegistryToProject(
  projectId: string,
  snapshot: CogoXyzRegistrySnapshot,
  options?: { expectedSettingsRevision: StudioProjectSettingsRevisionRef },
): Promise<GeneratedRegistryPersistence> {
  let expectedSettingsRevision = options?.expectedSettingsRevision
  try {
    if (!expectedSettingsRevision) {
      expectedSettingsRevision = (await getProject(projectId))?.settingsRevision
      if (!expectedSettingsRevision) {
        throw new Error(`COGO_XYZ_SETTINGS_REVISION_UNAVAILABLE:${projectId}`)
      }
    }
    await saveCogoXyzRegistryToProject(projectId, snapshot, {
      expectedSettingsRevision,
    })
    return { status: 'saved', projectId, retryable: false }
  } catch (error) {
    return {
      status: 'sync_pending',
      projectId,
      retryable: true,
      error: error instanceof Error ? error.message : String(error),
      ...(expectedSettingsRevision ? { expectedSettingsRevision } : {}),
    }
  }
}

export function currentServerProjectId(): string | null {
  const state = useStudioStore.getState()
  if (state.projectSource !== 'server') return null
  return state.currentProjectId ?? null
}

export async function saveCogoXyzRegistryToCurrentProject(
  registry: CogoXyzRegistry = cogoXyzRegistry,
): Promise<boolean> {
  const projectId = currentServerProjectId()
  if (!projectId) return false
  const snapshot = registry.snapshot()
  await saveCogoXyzRegistryToProject(projectId, snapshot)
  void pushCogoXyzRegistrySnapshotToServer(snapshot)
  return true
}

export async function loadCogoXyzRegistryFromCurrentProject(
  registry: CogoXyzRegistry = cogoXyzRegistry,
): Promise<boolean> {
  const projectId = currentServerProjectId()
  if (!projectId) return false
  return loadCogoXyzRegistryFromProject(projectId, registry)
}
